import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const runtimeCfg: any = (typeof window !== 'undefined' ? (window as any).__APP_CONFIG__ : undefined) || undefined;
//const API_BASE_URL = (runtimeCfg?.API_BASE_URL as string) || process.env.REACT_APP_API_URL || 'http://posnew_sistema.test/backend/public/api';
const API_BASE_URL = 'https://poschile.net/pos/api';

export interface LoginCredentials {
  nick_usuario: string;
  pass_usuario: string;
}

export interface AuthResponse {
  estado: number;
  usuario: {
    id_usuario: number;
    nombre_usuario: string;
    nick_usuario: string;
    tipo: number;
  };
}

export interface Producto {
  id_producto: number;
  nombre_producto: string;
  precio_venta_producto: number;
  precio_costo_producto: number;
  codigo_barra_producto: string;
  inventario: number;
  id_categoria: number;
  stock_estado_producto: number;
  id_unidad_medida?: number;
}

export interface ProductFilters {
  id?: number;
  nombre?: string;
  nombre_producto?: string;
  codigo_barra?: string;
  codigo_barra_producto?: string;
  id_categoria?: number;
  estado?: number;
  page?: number;
  per_page?: number;
}

export interface ApiResponse<T> {
  estado: number;
  data?: T;
  registros?: T;
  msg?: string;
  error?: any;
  boleta?: any;
}

export interface SaleItem {
  cartId: string; // ID único del item en el carrito
  id_producto: number;
  cantidad_producto: number;
  precio_producto: number;
  descuento_producto?: number;
  nombre_producto?: string;
}

export interface SaleData {
  id_usuario: number;
  id_caja: number;
  id_medio_pago: number;
  nombre_cliente_boleta: string;
  total_boleta: number;
  pago_boleta: number;
  vuelto_boleta: number;
  descuento_boleta: number;
  productos: SaleItem[];
}

export interface ReceiptData {
  boletaId: string;
  fecha: string;
  productos: Array<{
    nombre: string;
    cantidad: number;
    precio: number;
  }>;
  total: number;
  pago: number;
  vuelto: number;
  printerName?: string;
}

export interface Boleta {
  id_boleta: number;
  total_boleta: number;
  id_medio_pago: number;
  descuento_boleta: number;
  pago_boleta: number;
  vuelto_boleta: number;
  id_estado_boleta: number;
  nombre_cliente_boleta: string;
  id_usuario: number;
  id_caja: number;
  fecha_ingreso_boleta: string;
  detalles?: DetalleBoleta[];
}

export interface DetalleBoleta {
  id_detalle_boleta: number;
  id_boleta: number;
  id_producto: number;
  cantidad_producto: number;
  precio_producto: number;
  descuento_producto: number;
  producto?: Producto;
}

export interface Usuario {
  id_usuario: number;
  nombre_usuario: string;
  apellido_usuario: string;
  nick_usuario: string;
  rut_usuario: string;
  tipo: number;
  estado: number;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        try { window.dispatchEvent(new CustomEvent('global-loading', { detail: { delta: 1 } })); } catch {}
        return config;
      },
      (error) => {
        try { window.dispatchEvent(new CustomEvent('global-loading', { detail: { delta: -1 } })); } catch {}
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        try { window.dispatchEvent(new CustomEvent('global-loading', { detail: { delta: -1 } })); } catch {}
        return response;
      },
      (error) => {
        try { window.dispatchEvent(new CustomEvent('global-loading', { detail: { delta: -1 } })); } catch {}
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  }

  // Productos endpoints
  async getProducts(filters: ProductFilters = {}): Promise<ApiResponse<Producto[]>> {
    const params = new URLSearchParams();
    // Mapear alias a nombres reales de backend
    const mapped: Record<string, any> = { ...filters } as any;
    if (mapped.nombre && !mapped.nombre_producto) mapped.nombre_producto = mapped.nombre;
    if (mapped.codigo_barra && !mapped.codigo_barra_producto) mapped.codigo_barra_producto = mapped.codigo_barra;
    delete mapped.nombre;
    delete mapped.codigo_barra;
    Object.entries(mapped).forEach(([key, value]) => {
      if (value !== undefined && value !== null) params.append(key, String(value));
    });
    
    const response = await this.api.get<ApiResponse<Producto[]>>(`/productos?${params.toString()}`);
    const data = response.data;
    const registros: any = (data as any)?.registros;
    const normalized = Array.isArray((registros as any)?.data) ? (registros as any).data : registros;
    return { ...data, registros: normalized } as ApiResponse<Producto[]>;
  }

  async getProduct(id: number): Promise<ApiResponse<Producto>> {
    const response = await this.api.get<ApiResponse<Producto>>(`/productos/${id}`);
    return response.data;
  }

  async createProduct(product: Partial<Producto>): Promise<ApiResponse<Producto>> {
    const response = await this.api.post<ApiResponse<Producto>>('/productos', product);
    return response.data;
  }

  async updateProduct(id: number, product: Partial<Producto>): Promise<ApiResponse<Producto>> {
    const response = await this.api.put<ApiResponse<Producto>>(`/productos/${id}`, product);
    return response.data;
  }

  async changeProductStatus(id: number, estado: number): Promise<ApiResponse<any>> {
    const response = await this.api.patch<ApiResponse<any>>(`/productos/${id}/estado`, { estado });
    return response.data;
  }

  // Boletas endpoints
  async getBoletas(filters: any = {}): Promise<ApiResponse<Boleta[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    
    const response = await this.api.get<ApiResponse<Boleta[]>>(`/boletas?${params.toString()}`);
    const data = response.data;
    const registros: any = (data as any)?.registros;
    const normalized = Array.isArray((registros as any)?.data) ? (registros as any).data : registros;
    return { ...data, registros: normalized } as ApiResponse<Boleta[]>;
  }

  async getBoleta(id: number): Promise<ApiResponse<Boleta>> {
    const response = await this.api.get<ApiResponse<Boleta>>(`/boletas/${id}`);
    const data: any = response.data;
    const registro = data?.registros ?? data?.data ?? null;
    return { ...data, data: registro } as ApiResponse<Boleta>;
  }

  async getBoletaDetalles(id: number): Promise<ApiResponse<Boleta>> {
    const response = await this.api.get<ApiResponse<Boleta>>(`/boletas/${id}/detalles`);
    const data: any = response.data;
    const detalles = data?.detalles ?? data?.data ?? [];
    return { ...data, data: detalles } as ApiResponse<any>;
  }

  async createBoleta(saleData: SaleData): Promise<ApiResponse<Boleta>> {
    const response = await this.api.post<ApiResponse<Boleta>>('/boletas', saleData);
    return response.data;
  }

  async changeBoletaStatus(id: number, estado: number): Promise<ApiResponse<any>> {
    const response = await this.api.patch<ApiResponse<any>>(`/boletas/${id}/estado`, { estado });
    return response.data;
  }

  // Sistema endpoints
  async verifySupervisor(password: string): Promise<ApiResponse<any>> {
    const response = await this.api.post<ApiResponse<any>>('/sistema/verify-supervisor', { password });
    return response.data;
  }

  async getVerificarStock(): Promise<{ estado: number; verificar_stock: number }> {
    const response = await this.api.get<{ estado: number; verificar_stock: number }>(
      '/sistema/verificar-stock'
    );
    return response.data as any;
  }

  async getVentasCajaHoy(idCaja: number, fecha?: string): Promise<{ ok: boolean; id_caja: number; fecha: string; total_ventas: number; message?: string }> {
    const query = fecha ? `?fecha=${fecha}` : '';
    const response = await this.api.get<{ ok: boolean; id_caja: number; fecha: string; total_ventas: number; message?: string }>(
      `/caja/${idCaja}/ventas-hoy${query}`
    );
    return response.data;
  }

  // Usuarios endpoints
  async getUsuarios(filters: any = {}): Promise<ApiResponse<Usuario[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    
    const response = await this.api.get<ApiResponse<Usuario[]>>(`/usuarios?${params.toString()}`);
    const data = response.data;
    const registros: any = (data as any)?.registros;
    const normalized = Array.isArray((registros as any)?.data) ? (registros as any).data : registros;
    return { ...data, registros: normalized } as ApiResponse<Usuario[]>;
  }

  async getUsuario(id: number): Promise<ApiResponse<Usuario>> {
    const response = await this.api.get<ApiResponse<Usuario>>(`/usuarios/${id}`);
    return response.data;
  }

  async createUsuario(usuario: Partial<Usuario>): Promise<ApiResponse<Usuario>> {
    const response = await this.api.post<ApiResponse<Usuario>>('/usuarios', usuario);
    return response.data;
  }

  async updateUsuario(id: number, usuario: Partial<Usuario>): Promise<ApiResponse<Usuario>> {
    const response = await this.api.put<ApiResponse<Usuario>>(`/usuarios/${id}`, usuario);
    return response.data;
  }

  async changeUsuarioStatus(id: number, estado: number): Promise<ApiResponse<any>> {
    const response = await this.api.patch<ApiResponse<any>>(`/usuarios/${id}/estado`, { estado });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
